import type { ReloadOptions } from "@inertiaui/modal-react";
import { Dialog, type DialogRootProps } from "@ui/components/Dialog";
import { Page } from "@ui/components/Page";
import type { ReactNode } from "react";
import { RoutedModalBridge } from "@/components/RoutedModalBridge/RoutedModalBridge";

interface RoutedModalRenderProps {
  close: () => void;
  emit: (event: string, ...args: unknown[]) => void;
  reload: (options?: ReloadOptions) => void;
}

export interface RoutedModalProps {
  children: ReactNode | ((props: RoutedModalRenderProps) => ReactNode);
  size?: DialogRootProps["size"];
  /** "card" (default) or "bare" — drops the surface for illustrated modals. */
  chrome?: DialogRootProps["chrome"];
  /**
   * Mobile presentation. "sheet" (default): a bottom card sheet sized to its
   * content — right for short tasks. "page": a fullscreen page-from-bottom —
   * use when the content always fills the viewport (tall forms like edit
   * profile or add trip). Desktop renders the same detached card either way.
   */
  presentation?: "sheet" | "page";
}

/**
 * Bridge between Inertia Modal's HeadlessModal and the system Dialog.
 *
 * Routed modal page components wrap their content with this component to
 * render inside the platform-adaptive Dialog (mobile native-style sheet,
 * desktop detached dialog). Inertia Modal handles routing, URL state, and the
 * modal stack; Silk handles travel, dismissal gestures, focus containment,
 * scroll locking, and ARIA semantics.
 *
 * Usage in a modal page component:
 *
 *   export default function TripsNew({ ... }) {
 *     return (
 *       <RoutedModal size="sm">
 *         {({ close }) => (
 *           <>
 *             <Dialog.Header title="..." onClose={close} />
 *             <Dialog.Body>...</Dialog.Body>
 *             <Dialog.Footer>...</Dialog.Footer>
 *           </>
 *         )}
 *       </RoutedModal>
 *     );
 *   }
 *   TripsNew.layout = (page: ReactNode) => page;
 */
export function RoutedModal({ children, size, chrome, presentation = "sheet" }: RoutedModalProps) {
  // Page composes the Dialog part set (shared context), so the children —
  // Dialog.Header/Body/Footer — work identically under either root.
  const Surface = presentation === "page" ? Page : Dialog;
  return (
    <RoutedModalBridge>
      {({ afterLeave, close, emit, isOpen, onOpenChange, reload }) => (
        <Surface.Root
          chrome={chrome}
          onExitComplete={afterLeave}
          onOpenChange={onOpenChange}
          isOpen={isOpen}
          size={size}
        >
          <Surface.Content>
            {typeof children === "function" ? children({ close, emit, reload }) : children}
          </Surface.Content>
        </Surface.Root>
      )}
    </RoutedModalBridge>
  );
}
